import React, { useCallback, useRef, useState } from "react";
import WordCloud from "react-d3-cloud";
import { Paper } from "@mui/material";
import { scaleOrdinal } from "d3-scale";
import { schemeCategory10 } from "d3-scale-chromatic";
import { truncate } from "lodash";
import { forEach } from "lodash";
const MINSIZE = 20;
const FONT = "serif";
const CANVASWIDTH = 700; // width of the layout (px)
const CANVASHEIGHT = 250; // height of the layout (px)

const Word = React.memo(
  function Word({ data, onWordMouseOver, onWordMouseOut }) {
    const fontSize = useCallback((d) => d.size);
    const schemeCategory10ScaleOrdinal = useCallback(
      scaleOrdinal(schemeCategory10),
      []
    );
    const fill = useCallback((d, i) => schemeCategory10ScaleOrdinal(i), []);
    return (
      <WordCloud
        data={[...data]}
        width={CANVASWIDTH}
        height={CANVASHEIGHT}
        onWordMouseOver={onWordMouseOver}
        onWordMouseOut={onWordMouseOut}
        font={FONT}
        fontSize={fontSize}
        rotate={() => 0} // display all words horizontally
        random={Math.random}
        fill={fill}
      />
    );
  },
  function same(prev, next) {
    return JSON.stringify(prev.data) === JSON.stringify(next.data);
  }
);

export default function WordcloudBoard({
  data,
  table,
  selectedLabels,
  choiceLabelsChecked,
  openLabelsChecked,
}) {
  const [open, setOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState("");
  const [counts, setCounts] = useState([]);
  const [countsObj, setCountsObj] = useState({});

  const [x, setX] = useState();
  const [y, setY] = useState();
  // get the position of the mouse for the tooltup
  React.useEffect(() => {
    const update = (e) => {
      setX(e.x);
      setY(e.y);
    };
    window.addEventListener("mousemove", update);
    window.addEventListener("touchmove", update);
    return () => {
      window.removeEventListener("mousemove", update);
      window.removeEventListener("touchmove", update);
    };
  }, [setX, setY]);

  React.useEffect(() => {
    const openLabelIds = openLabelsChecked.map(({ id }) => id);
    const openValues = data
      .flatMap((beep) =>
        beep.table.filter((row) => openLabelIds.includes(row.id))
      )
      .map(({ score }) => score);
    const concat = openValues.join(" ");

    const choiceLabelIds = choiceLabelsChecked.map(({ id }) => id);
    const choiceValues = data
      .flatMap((beep) =>
        beep.table.filter((row) => choiceLabelIds.includes(row.id))
      )
      .flatMap(({ options, primaryScore }) =>
        primaryScore.map((index) => options[index - 1])
      );
    const words = [...concat.split(" "), ...choiceValues];

    const countsObj = {};
    words.forEach(function (x) {
      countsObj[x] = (countsObj[x] || 0) + 1;
    });
    setCountsObj(countsObj);

    // calculate the fontSize
    const maxValue = Math.max(...Object.values(countsObj));
    const fontSize = (value) => {
      let size = (value / maxValue) * 50;
      if (size < MINSIZE) size = MINSIZE;
      return size;
    };

    // calculate the width
    const getTextWidth = (text, font) => {
      const element = document.createElement("canvas");
      const context = element.getContext("2d");
      context.font = font;
      return context.measureText(text).width + 85;
    };

    const getTruncatedText = (text, font, size) => {
      let truncatedText = text;
      while (
        getTextWidth(truncatedText, font) >
        CANVASWIDTH - getTextWidth(" ...", font)
      ) {
        if (truncatedText.length <= 1) {
          // Cannot truncate further
          return truncatedText;
        }
        truncatedText = truncatedText.substring(0, truncatedText.length - 1);
      }
      return truncatedText + "..."; // Add ellipsis to indicate truncation
    };

    const countsArr = Object.keys(countsObj).map((key) => {
      const size = fontSize(countsObj[key]);
      const width = getTextWidth(key, `${size}px ${FONT}`);
      //console.log(width);
      const text =
        width > CANVASWIDTH ? getTruncatedText(key, `${size}px ${FONT}`) : key; // short text
      return {
        key: key, // word key
        size: size, // fontSize
        value: countsObj[key], // frequency
        fullText: key,
        text: text,
      };
    });
    //console.log(countsArr);
    setCounts(countsArr);
  }, [data, table, selectedLabels]);
  const onWordMouseOver = useCallback((_, d) => {
    setOpen(true);
    setSelectedWord(d.text);
  }, []);
  const onWordMouseOut = useCallback(() => {
    setOpen(false);
    setSelectedWord("");
  }, []);
  return (
    <div>
      <Word
        data={counts}
        onWordMouseOver={onWordMouseOver}
        onWordMouseOut={onWordMouseOut}
      />
      {open && x && y ? (
        <Paper
          elevation={5}
          style={{
            position: "fixed",
            top: y + 10,
            left: x + 10,
            padding: 16,
          }}
        >
          {counts.filter((count) => count.text === selectedWord)?.[0]?.value ??
            ""}
          x{" "}
          {counts.filter((count) => count.text === selectedWord)?.[0]
            ?.fullText ?? ""}
        </Paper>
      ) : null}
    </div>
  );
}
